import { AllApplicationTable } from "@/features/admin/AllApplicationTable";
import { useAllApplication } from "@/features/admin/hook/useAllApplication";
import { useDebounce } from "@/hooks/use-debounce";
import { useEffect } from "react";
import { Button, Calendar, Dropdown, Grid, Input, Typography } from "antd";
import { ArrowDownToLine, ChevronDown, RotateCcw, Search } from "lucide-react";
import { useState } from "react";
import { CATEGORY_ITEMS } from "@/constants/category";
import dayjs from "dayjs";
import { transformApplicationSearch } from "@/utils/applicationStatus";
import { downloadExcel } from "@/utils/downloadExcel";
import LoadingFullScreen from "@/components/ui/LoadingFullScreen";

const { Title } = Typography;
const { useBreakpoint } = Grid;

function renderDownloadIcon() {
  return <ArrowDownToLine size={18} aria-hidden="true" />;
}

function renderArrowDownIcon() {
  return <ChevronDown size={18} aria-hidden="true" />;
}

function renderReloadIcon() {
  return <RotateCcw size={18} aria-hidden="true" />;
}

export default function AllApplication() {
  const screens = useBreakpoint();
  const { fetchAllList, loading, applicationAllList } = useAllApplication();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [tableResetKey, setTableResetKey] = useState(0);

  const debouncedSearch = useDebounce(search, 300);
  const pageSize = 25;

  const categoryKey = selectedCategory === "" ? "" : selectedCategory;

  const buildSearchValue = (statusValue: string | null = selectedStatus) =>
    transformApplicationSearch(
      [debouncedSearch, statusValue ?? ""].filter(Boolean).join(" "),
    );

  const searchValue = buildSearchValue();

  useEffect(() => {
    setCurrentPage(1);

    fetchAllList(searchValue, 1, pageSize, categoryKey, selectedDate ?? "");
  }, [searchValue]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    fetchAllList(searchValue, page, pageSize, categoryKey, selectedDate ?? "");
  };

  const handleCategoryChange = (key: string) => {
    setSelectedCategory(key);
    setCurrentPage(1);

    fetchAllList(
      searchValue,
      1,
      pageSize,
      key === "" ? "" : key,
      selectedDate ?? "",
    );
  };

  const handleStatusFilterChange = (value: string | null) => {
    const nextStatus = value ?? "";
    setSelectedStatus(nextStatus);
    setCurrentPage(1);

    const nextSearchValue = buildSearchValue(nextStatus);

    fetchAllList(nextSearchValue, 1, pageSize, categoryKey, selectedDate ?? "");
  };

  const handleResetFilters = () => {
    setSearch("");
    setSelectedCategory("");
    setSelectedStatus("");
    setSelectedDate(null);
    setCurrentPage(1);
    setIsDateDropdownOpen(false);
    setTableResetKey((prev) => prev + 1);

    fetchAllList(buildSearchValue(""), 1, pageSize, "", "");
  };

  const selectedCategoryLabel =
    CATEGORY_ITEMS.find((item) => item?.key === selectedCategory)?.label ??
    "Semua kategori";

  const handleDownloadExcel = async () => {
    try {
      setDownloadLoading(true);

      await Promise.all([
        downloadExcel(applicationAllList?.excelUrl, "semua-permohonan.xlsx"),
        new Promise((resolve) => setTimeout(resolve, 800)),
      ]);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <>
        <LoadingFullScreen
      visible={downloadLoading}
      message="Sedang memuat turun Excel..."
    />

    <section className="px-4 sm:px-10 py-6 flex flex-col">
      <div className="flex items-center justify-between gap-4">
        <Title
          level={2}
          style={{
            fontWeight: 700,
            marginBottom: 0,
            fontSize: screens.sm ? 24 : 18,
            color: "var(--color-primary)",
          }}
        >
          Semua Permohonan
        </Title>

        <div className="text-sm font-medium text-right text-[#666]">
          Contoh ID TT: TB1000 | T: Tekek (Kampung) , B: Bekerja, N:Ikut
          Keluarga, 1000 Nombor siri ahli
        </div>
      </div>

      <div className="flex items-center flex-wrap justify-between gap-4 mt-4">
        <div className="flex gap-2.5 items-center">
          <Button
            icon={renderDownloadIcon()}
            type="primary"
            size="large"
            className="min-w-48"
            loading={downloadLoading}
            onClick={handleDownloadExcel}
          >
            Muat Turun Excel
          </Button>

          {/* <Button
            icon={renderDownloadIcon()}
            type="primary"
            size="large"
            className="min-w-48"
          >
            Muat Turun PDF
          </Button> */}
        </div>

        <div className="flex gap-2.5 items-center">
          <Dropdown
            trigger={["click"]}
            open={isDateDropdownOpen}
            onOpenChange={setIsDateDropdownOpen}
            popupRender={() => (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="w-80">
                  <Calendar
                    fullscreen={false}
                    disabledDate={(current) => current.isAfter(dayjs(), "day")}
                    onSelect={(date) => {
                      const formattedDate = date.format("YYYY-MM-DD");

                      setSelectedDate(formattedDate);
                      setCurrentPage(1);

                      fetchAllList(
                        searchValue,
                        1,
                        pageSize,
                        categoryKey,
                        formattedDate,
                      );

                      // Hide calendar after selecting date
                      setIsDateDropdownOpen(false);
                    }}
                  />

                  <div className="border-t px-4 py-2 flex justify-end">
                    <Button
                      type="link"
                      onClick={() => {
                        setSelectedDate(null);
                        setCurrentPage(1);

                        fetchAllList(searchValue, 1, pageSize, categoryKey, "");

                        // Hide calendar after clearing
                        setIsDateDropdownOpen(false);
                      }}
                    >
                      Set Semula
                    </Button>
                  </div>
                </div>
              </div>
            )}
          >
            <Button
              icon={renderArrowDownIcon()}
              type="primary"
              size="large"
              className="min-w-48"
              iconPlacement="end"
            >
              {selectedDate
                ? dayjs(selectedDate).format("DD MMM YYYY")
                : "Tapis mengikut tarikh"}
            </Button>
          </Dropdown>

          <Dropdown
            trigger={["click"]}
            menu={{
              items: CATEGORY_ITEMS,
              onClick: ({ key }) => {
                handleCategoryChange(key);
              },
            }}
          >
            <Button
              icon={renderArrowDownIcon()}
              type="primary"
              size="large"
              className="min-w-48"
              iconPlacement="end"
            >
              {selectedCategoryLabel}
            </Button>
          </Dropdown>

          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari"
            size="large"
            prefix={
              <Search
                size={16}
                style={{ color: "#C2C2C2" }}
                aria-hidden="true"
              />
            }
            className="max-w-2xs"
          />
          <Button
            size="large"
            className="min-w-48"
            icon={renderReloadIcon()}
            style={{ color: "#0081B3" }}
            onClick={handleResetFilters}
          >
            Set Semula
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <AllApplicationTable
          applications={applicationAllList?.applications ?? []}
          loading={loading}
          totalCount={applicationAllList?.totalCount ?? 0}
          currentPage={currentPage}
          pageSize={pageSize}
          resetKey={tableResetKey}
          onPageChange={handlePageChange}
          onStatusFilterChange={handleStatusFilterChange}
          onRefresh={() =>
            fetchAllList(
              searchValue,
              currentPage,
              pageSize,
              categoryKey,
              selectedDate ?? "",
            )
          }
        />
      </div>
    </section>
    </>
  );
}
